import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

export const FEATURE_FLAG_KEYS = {
  AI_FEATURES_MASTER: 'AI_FEATURES_MASTER',
  ASK_AI: 'ASK_AI',
  TREE_ASSISTANT: 'TREE_ASSISTANT',
  SEMANTIC_FIND: 'SEMANTIC_FIND',
  GENERATE_SUBTREE_FROM_CONTENT: 'GENERATE_SUBTREE_FROM_CONTENT',
  SUGGEST_CATEGORY_TREE_FROM_FILES: 'SUGGEST_CATEGORY_TREE_FROM_FILES',
  EXPAND_CATEGORY_TREE_FROM_FILES: 'EXPAND_CATEGORY_TREE_FROM_FILES',
  EXPORT_TO_FILE: 'EXPORT_TO_FILE',
  FILTER_CONTENT_PANE_BY_SUBCASE: 'FILTER_CONTENT_PANE_BY_SUBCASE',
  COMPARE_CONTENT_SELECTION: 'COMPARE_CONTENT_SELECTION',
  DOCUMENT_SHARING: 'DOCUMENT_SHARING',
  DOCUMENT_MERGE_COMPARE: 'DOCUMENT_MERGE_COMPARE',
} as const;

export type FeatureFlagKeyName = (typeof FEATURE_FLAG_KEYS)[keyof typeof FEATURE_FLAG_KEYS];

interface FeatureFlagsContextValue {
  /** Raw map from server (missing keys treated as enabled until first load completes). */
  flags: Record<string, boolean>;
  loaded: boolean;
  refresh: () => Promise<void>;
  isEnabled: (key: FeatureFlagKeyName | string) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setFlags({});
      setLoaded(false);
      return;
    }
    try {
      const { data } = await api.get<Record<string, boolean>>('/feature-flags');
      setFlags(data && typeof data === 'object' ? data : {});
      setLoaded(true);
    } catch {
      setFlags({});
      setLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      void refresh();
    } else {
      setFlags({});
      setLoaded(false);
    }
  }, [isLoading, isAuthenticated, refresh]);

  const isEnabled = useCallback(
    (key: FeatureFlagKeyName | string) => {
      if (!loaded) return true;
      return flags[key] !== false;
    },
    [flags, loaded],
  );

  const value = useMemo(
    () => ({
      flags,
      loaded,
      refresh,
      isEnabled,
    }),
    [flags, loaded, refresh, isEnabled],
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return ctx;
}
